// Client API for the admin Company settings screen (COMPANY-SETTINGS-ADMIN PART 1).
//
// ⚠ TWO ROUTES, TWO AUDIENCES. `/api/company-settings` is the staff-gated, offline-cached,
// deliberately minimal board read (see opsBoardTypes.CompanySettings) and is NOT touched here.
// This module talks only to `/api/company-settings/admin` and the admin PATCH.
//
// The held authority fields — waitlist_allocation_policy, notification_policy,
// training_activity_mode, participant_hierarchy_mode — are absent from this type on purpose.
// The server's zod whitelist is what enforces that; this type simply does not tempt anyone.

import { apiFetch } from './api';

export type ChipChannel = 'booking_kind' | 'instructor' | 'base';

export const CHIP_CHANNEL_OPTIONS: { value: ChipChannel; label: string }[] = [
  { value: 'booking_kind', label: 'Booking type' },
  { value: 'instructor', label: 'Instructor' },
  { value: 'base', label: 'Base' },
];

export interface AdminCompanySettings {
  // Booking defaults
  default_booking_mins: number;
  default_buffer_pre_mins: number;
  default_buffer_post_mins: number;
  // Board appearance
  chip_top_bar_channel: ChipChannel;
  chip_fill_channel: ChipChannel;
  // Units
  unit_weight: string;
  unit_volume: string;
  unit_fuel_avgas: string;
  unit_fuel_jet: string;
  unit_temp: string;
  unit_distance: string;
  unit_altitude: string;
  unit_speed: string;
  // Operations
  mr_escalation_hours: number;
  fuel_discrepancy_threshold: number;
  tr_photo_required: boolean;
  mr_photo_required: boolean;
}

// The unit options, mirroring the Prisma enums the server validates against
// (WeightUnit, VolumeUnit, AvgasFuelUnit, JetFuelUnit, TempUnit, DistanceUnit, AltitudeUnit,
// SpeedUnit). ⚠ These are NOT interchangeable: AVGAS has no imp_gal, jet fuel has no kg, and
// distance is 'miles', not 'sm'. A value outside the server's enum is a 400, not a silent save.
export const UNIT_OPTIONS: Record<string, { value: string; label: string }[]> = {
  unit_weight:     [{ value: 'kg', label: 'Kilograms' }, { value: 'lbs', label: 'Pounds' }],
  unit_volume:     [{ value: 'litres', label: 'Litres' }, { value: 'us_gal', label: 'US gallons' }, { value: 'imp_gal', label: 'Imperial gallons' }],
  unit_fuel_avgas: [{ value: 'litres', label: 'Litres' }, { value: 'us_gal', label: 'US gallons' }],
  unit_fuel_jet:   [{ value: 'litres', label: 'Litres' }, { value: 'lbs', label: 'Pounds' }, { value: 'us_gal', label: 'US gallons' }],
  unit_temp:       [{ value: 'celsius', label: 'Celsius' }, { value: 'fahrenheit', label: 'Fahrenheit' }],
  unit_distance:   [{ value: 'nm', label: 'Nautical miles' }, { value: 'km', label: 'Kilometres' }, { value: 'miles', label: 'Statute miles' }],
  unit_altitude:   [{ value: 'ft', label: 'Feet' }, { value: 'm', label: 'Metres' }],
  unit_speed:      [{ value: 'kts', label: 'Knots' }, { value: 'kmh', label: 'km/h' }, { value: 'mph', label: 'mph' }],
};

export const UNIT_LABELS: Record<string, string> = {
  unit_weight: 'Weight',
  unit_volume: 'Volume',
  unit_fuel_avgas: 'AVGAS',
  unit_fuel_jet: 'Jet A1',
  unit_temp: 'Temperature',
  unit_distance: 'Distance',
  unit_altitude: 'Altitude',
  unit_speed: 'Speed',
};

export async function getAdminCompanySettings(): Promise<AdminCompanySettings | null> {
  const r = await apiFetch<AdminCompanySettings>('/api/company-settings/admin');
  return r.error || !r.data ? null : r.data;
}

export async function patchCompanySettings(
  patch: Partial<AdminCompanySettings>,
): Promise<{ ok: boolean; data?: AdminCompanySettings; error?: string }> {
  const r = await apiFetch<AdminCompanySettings>('/api/company-settings', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return r.error ? { ok: false, error: r.error.message } : { ok: true, data: r.data ?? undefined };
}
